import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { MainMatchesData } from './MainMatchesData';
import {
  FaHome,
  FaPlane,
  FaHistory,
  FaRandom,
  FaHandshake,
  FaCalculator,
} from 'react-icons/fa';
import { MdSportsScore, MdLeaderboard } from 'react-icons/md';

interface Props {
  selectedItemData: MainMatchesData;
  columnToShow?: 'left' | 'center' | 'right' | 'all';
}

export const LastMatchesAndResultPrevision = ({
  selectedItemData,
  columnToShow = 'all',
}: Props) => {
  const { t } = useTranslation();

  if (!selectedItemData) {
    return <div>{t('ui.noDataAvailable')}</div>;
  }

  const showLeft = columnToShow === 'left' || columnToShow === 'all';
  const showCenter = columnToShow === 'center' || columnToShow === 'all';
  const showRight = columnToShow === 'right' || columnToShow === 'all';

  const TruncatedString = (value: string) => {
    if (value.length <= 15) {
      return <span>{value}</span>;
    } else {
      const truncatedText = value.substring(0, 15) + '...';
      return <span title={value}>{truncatedText}</span>;
    }
  };

  const getCellColorClass = (value: number): string => {
    if (value <= 10) return 'dark-red cell-text';
    if (value <= 20) return 'medium-red cell-text';
    if (value <= 30) return 'light-red cell-text';
    if (value <= 40) return 'dark-orange cell-text';
    if (value <= 50) return 'light-orange cell-text';
    if (value <= 60) return 'dark-yellow cell-text';
    if (value <= 70) return 'light-yellow cell-text';
    if (value <= 80) return 'light-green cell-text';
    if (value <= 90) return 'medium-green cell-text';
    return 'dark-green cell-text';
  };

  const getMatchResultClass = (value: string): string => {
    if (value.includes('W'))
      return 'bg-green-600 bg-opacity-50 text-green-300 font-bold';
    if (value.includes('L'))
      return 'bg-red-600 bg-opacity-50 text-red-300 font-bold';
    if (value.includes('N'))
      return 'bg-yellow-400 bg-opacity-50 text-yellow-300 font-bold';
    return 'text-gray-300';
  };

  const getHeaderStyle = (isHomeTeam: boolean) => {
    return isHomeTeam
      ? { background: 'linear-gradient(to right, #065f46, #064e3b)' }
      : { background: 'linear-gradient(to right, #1e40af, #1e3a8a)' };
  };

  const HeaderWithIcon = ({
    icon: Icon,
    text,
    style = {},
  }: {
    icon: React.ElementType;
    text: string;
    style?: React.CSSProperties;
  }) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...style,
      }}
    >
      <Icon style={{ marginRight: '0.5rem' }} /> {text}
    </div>
  );

  const ColumnHeaderWithIcon = ({
    icon: Icon,
    text,
  }: {
    icon: React.ElementType;
    text: string;
  }) => (
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon style={{ marginRight: '0.25rem' }} /> {text}
    </span>
  );

  return (
    <div className="LastMatchesAndResultPrevision">
      <div className="last5container">
        {/* LEFT COLUMN - HOME TEAM */}
        {showLeft && (
          <div className="left-table">
            {/* Last 5 home matches */}
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th colSpan={3} style={getHeaderStyle(true)}>
                      <HeaderWithIcon
                        icon={FaHome}
                        text={t('matches.last5HomeMatches')}
                      />
                    </th>
                  </tr>
                  <tr>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaHome}
                        text={t('matches.home')}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaPlane}
                        text={t('matches.away')}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={MdSportsScore}
                        text={t('matches.result')}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {selectedItemData.Last5HomeMatches.map((match, index) => (
                    <tr key={`home-${index}`} className="hover:bg-slate-700">
                      <td className="text-white">
                        {TruncatedString(match.HomeTeam)}
                      </td>
                      <td className="text-gray-300">
                        {TruncatedString(match.AwayTeam)}
                      </td>
                      <td className={getMatchResultClass(match.WLN)}>
                        {match.Result}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Last 5 matches of home team */}
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th colSpan={3} style={getHeaderStyle(true)}>
                      <HeaderWithIcon
                        icon={FaHistory}
                        text={t('matches.last5Matches')}
                      />
                    </th>
                  </tr>
                  <tr>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaHome}
                        text={t('matches.home')}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaPlane}
                        text={t('matches.away')}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={MdSportsScore}
                        text={t('matches.result')}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {selectedItemData.Last5MatchesHomeTeam.map((match, index) => (
                    <tr
                      key={`home-team-${index}`}
                      className="hover:bg-slate-700"
                    >
                      <td className="text-white">
                        {TruncatedString(match.HomeTeam)}
                      </td>
                      <td className="text-gray-300">
                        {TruncatedString(match.AwayTeam)}
                      </td>
                      <td className={getMatchResultClass(match.WLN)}>
                        {match.Result}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* CENTER COLUMN - MATCH INFO AND PREDICTIONS */}
        {showCenter && (
          <div className="center-table">
            {/* Team Form */}
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th
                      className="bg-gradient-to-r from-teal-800 to-indigo-900"
                      colSpan={2}
                    >
                      <HeaderWithIcon
                        icon={MdLeaderboard}
                        text={t('matches.teamForm')}
                      />
                    </th>
                  </tr>
                  <tr>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaHome}
                        text={t('matches.home')}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaPlane}
                        text={t('matches.away')}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td
                      className={getCellColorClass(selectedItemData.HomePoints)}
                    >
                      <div className="biggerFont">
                        {selectedItemData.HomePoints}
                      </div>
                    </td>
                    <td
                      className={getCellColorClass(selectedItemData.AwayPoints)}
                    >
                      <div className="biggerFont">
                        {selectedItemData.AwayPoints}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Result Predictions */}
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th
                      className="bg-gradient-to-r from-teal-800 to-indigo-900"
                      colSpan={3}
                    >
                      <HeaderWithIcon
                        icon={MdSportsScore}
                        text={t('matches.outcome')}
                      />
                    </th>
                  </tr>
                  <tr>
                    <th className="text-teal-300">
                      <ColumnHeaderWithIcon icon={FaHome} text="1" />
                    </th>
                    <th className="text-yellow-300">
                      <ColumnHeaderWithIcon icon={FaHandshake} text="X" />
                    </th>
                    <th className="text-indigo-300">
                      <ColumnHeaderWithIcon icon={FaPlane} text="2" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td
                      className={getCellColorClass(
                        selectedItemData.Result1X2.Default_1,
                      )}
                    >
                      {selectedItemData.Result1X2.Default_1}%
                    </td>
                    <td
                      className={getCellColorClass(
                        selectedItemData.Result1X2.Default_X,
                      )}
                    >
                      {selectedItemData.Result1X2.Default_X}%
                    </td>
                    <td
                      className={getCellColorClass(
                        selectedItemData.Result1X2.Default_2,
                      )}
                    >
                      {selectedItemData.Result1X2.Default_2}%
                    </td>
                  </tr>
                </tbody>
                <thead>
                  <tr>
                    <th
                      colSpan={3}
                      style={{ backgroundColor: 'rgba(51, 65, 85, 0.5)' }}
                    >
                      <HeaderWithIcon
                        icon={FaCalculator}
                        text={t('matches.poisson')}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td
                      className={getCellColorClass(
                        selectedItemData.Result1X2.Poisson_1,
                      )}
                    >
                      {selectedItemData.Result1X2.Poisson_1}%
                    </td>
                    <td
                      className={getCellColorClass(
                        selectedItemData.Result1X2.Poisson_X,
                      )}
                    >
                      {selectedItemData.Result1X2.Poisson_X}%
                    </td>
                    <td
                      className={getCellColorClass(
                        selectedItemData.Result1X2.Poisson_2,
                      )}
                    >
                      {selectedItemData.Result1X2.Poisson_2}%
                    </td>
                  </tr>
                </tbody>
                <thead>
                  <tr>
                    <th
                      colSpan={3}
                      style={{ backgroundColor: 'rgba(51, 65, 85, 0.5)' }}
                    >
                      <HeaderWithIcon
                        icon={FaRandom}
                        text={t('matches.combined')}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td
                      className={getCellColorClass(
                        selectedItemData.Result1X2.Combined_1,
                      )}
                    >
                      {selectedItemData.Result1X2.Combined_1}%
                    </td>
                    <td
                      className={getCellColorClass(
                        selectedItemData.Result1X2.Combined_X,
                      )}
                    >
                      {selectedItemData.Result1X2.Combined_X}%
                    </td>
                    <td
                      className={getCellColorClass(
                        selectedItemData.Result1X2.Combined_2,
                      )}
                    >
                      {selectedItemData.Result1X2.Combined_2}%
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* RIGHT COLUMN - AWAY TEAM */}
        {showRight && (
          <div className="right-table">
            {/* Last 5 away matches */}
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th colSpan={3} style={getHeaderStyle(false)}>
                      <HeaderWithIcon
                        icon={FaPlane}
                        text={t('matches.last5AwayMatches')}
                      />
                    </th>
                  </tr>
                  <tr>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaHome}
                        text={t('matches.home')}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaPlane}
                        text={t('matches.away')}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={MdSportsScore}
                        text={t('matches.result')}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {selectedItemData.Last5AwayMatches.map((match, index) => (
                    <tr key={`away-${index}`} className="hover:bg-slate-700">
                      <td className="text-white">
                        {TruncatedString(match.HomeTeam)}
                      </td>
                      <td className="text-gray-300">
                        {TruncatedString(match.AwayTeam)}
                      </td>
                      <td className={getMatchResultClass(match.WLN)}>
                        {match.Result}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Last 5 matches of away team */}
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th colSpan={3} style={getHeaderStyle(false)}>
                      <HeaderWithIcon
                        icon={FaHistory}
                        text={t('matches.last5Matches')}
                      />
                    </th>
                  </tr>
                  <tr>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaHome}
                        text={t('matches.home')}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaPlane}
                        text={t('matches.away')}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={MdSportsScore}
                        text={t('matches.result')}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {selectedItemData.Last5MatchesAwayTeam.map((match, index) => (
                    <tr
                      key={`away-team-${index}`}
                      className="hover:bg-slate-700"
                    >
                      <td className="text-white">
                        {TruncatedString(match.HomeTeam)}
                      </td>
                      <td className="text-gray-300">
                        {TruncatedString(match.AwayTeam)}
                      </td>
                      <td className={getMatchResultClass(match.WLN)}>
                        {match.Result}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
